import { useForm, yupResolver } from "@mantine/form";
import {
  useUpdateAccessPeriodMutation,
  useDeleteAccessPeriodMutation,
  GetAgreementQuery,
  useCreateAccessPeriodMutation,
  CreateAccessPeriodInput,
} from "src/generated/graphql";
import { memo, useMemo } from "react";
import { useModals } from "@mantine/modals";
import FormCard from "src/components/FormCard";
import { Button, Group, useMantineTheme, Flex } from "@mantine/core";
import { GetAgreement } from "src/api/graphql/queries/Agreement";
import { useEffect } from "react";
import { date, object, string } from "yup";
import Select from "src/components/Select";
import TimeInput from "src/components/TimeInput";
import { useAppContext } from "src/api/AppContext";
import { getTimeInAMPMFormat } from "src/utils/dateHelper";

export interface IAccessPeriodForm
  extends Omit<CreateAccessPeriodInput, "location"> {
  location: string;
}

type IForm = NonNullable<
  NonNullable<GetAgreementQuery["agreementOne"]>["accessPeriods"]
>[0];

interface AccessPeriodDetailProps {
  formData: IForm;
  handleModal?: (allotment: IForm) => void;
  isModal?: boolean;
  agreementId: string;
  isOrg?: boolean;
  isAllowWrite: boolean;
}

const AccessPeriodDetail = ({
  formData,
  isModal,
  handleModal,
  agreementId,
  isOrg,
  isAllowWrite,
}: AccessPeriodDetailProps) => {
  const isLocationSelectable =
    isOrg && window.location.pathname.includes("/org");
  const { currentLocation, currentOrganization } = useAppContext();
  const theme = useMantineTheme();
  const modals = useModals();
  const refetchQuery = [
    {
      query: GetAgreement,
      variables: { id: agreementId },
    },
  ];

  useEffect(() => {
    formData && isModal && getAccessPeriod();
  }, [formData, isModal]);

  const getAccessPeriod = () => {
    const { location, start, end } = formData;
    const _data = {
      location: location.id,
      start: new Date(start),
      end: new Date(end),
      agreement: { id: agreementId },
    };
    form.setValues(_data);
    form.resetDirty(_data);
  };

  const [createAccessPeriod, { loading: createLoading }] =
    useCreateAccessPeriodMutation({
      onCompleted: () => {
        modals.closeAll();
      },
      refetchQueries: refetchQuery,
    });

  const [updateAccessPeriod, { loading: updateLoading }] =
    useUpdateAccessPeriodMutation({
      onCompleted: () => {
        modals.closeAll();
      },
      refetchQueries: refetchQuery,
    });
  const [deleteAccessPeriod, { loading: deleteLoading }] =
    useDeleteAccessPeriodMutation({
      refetchQueries: refetchQuery,
    });

  const form = useForm<IAccessPeriodForm>({
    initialValues: {
      start: undefined,
      end: undefined,
      agreement: { id: agreementId },
      location: currentLocation ? currentLocation.id : "",
    },
    validate: yupResolver(
      object().shape({
        location: string().required("Location is required"),
        start: date().required("Start Time is required"),
        end: date()
          .required("End Time is required")
          .test(
            "end-time",
            "End Time cannot be earlier than Start Time",
            function (end) {
              const { start } = this.parent;
              if (start && end) return end >= start;
              return true;
            }
          ),
      })
    ),
  });

  const handleDelete = () => {
    deleteAccessPeriod({
      variables: { deleteAccessPeriodId: formData?.id! },
    });
  };

  const handleSubmit = async (values: Required<IAccessPeriodForm>) => {
    const { start, end, location, agreement } = values;
    let _obj = {
      start: new Date(start),
      end: new Date(end),
      location: { id: location },
      agreement,
    };

    formData?.id
      ? updateAccessPeriod({
          variables: {
            data: _obj,
            id: formData?.id,
          },
        })
      : createAccessPeriod({
          variables: {
            data: _obj,
          },
        });
  };

  const handleEdit = () => {
    handleModal && handleModal(formData);
  };

  const title = useMemo(
    () =>
      `${formData?.location.name} \u00A0\u00A0\u00A0 ${getTimeInAMPMFormat(
        formData?.start
      )} - ${getTimeInAMPMFormat(formData?.end)}`,
    []
  );

  return (
    <>
      <FormCard
        title={title}
        loading={updateLoading || deleteLoading}
        onDelete={
          isAllowWrite && isLocationSelectable ? handleDelete : undefined
        }
        onEdit={isAllowWrite && isLocationSelectable ? handleEdit : undefined}
        onSave={form.onSubmit((values) =>
          handleSubmit(values as Required<IAccessPeriodForm>)
        )}
        hideCard={isModal}
        mb={10}
        size="md"
        radius={4}
      >
        {isModal && (
          <>
            {isLocationSelectable && (
              <Select
                withAsterisk
                data={
                  currentOrganization?.locations?.map((val) => ({
                    value: val.id,
                    label: val.name,
                  })) || []
                }
                label="Location"
                {...form.getInputProps("location")}
              />
            )}

            <Flex justify={"space-between"} gap={"xs"}>
              <TimeInput
                withAsterisk
                format="12"
                label="Start Time"
                {...form.getInputProps("start")}
                styles={{ root: { width: "100%" } }}
              />

              <TimeInput
                withAsterisk
                format="12"
                label="End Time"
                {...form.getInputProps("end")}
                styles={{ root: { width: "100%" } }}
              />
            </Flex>

            <Group position="apart" mt="md" style={{ marginTop: 30 }}>
              <Button
                radius="md"
                variant="outline"
                onClick={() => modals.closeAll()}
                style={{
                  flex: 1,
                  color: theme.colors.secondaryTextColor[6],
                }}
                sx={{
                  backgroundColor: theme.colors.secondary[6],
                  "&:hover": {
                    background: theme.colors.secondary[5],
                  },
                }}
              >
                Cancel
              </Button>
              <Button
                radius="md"
                type="submit"
                disabled={createLoading || updateLoading}
                style={{
                  flex: 1,
                }}
                sx={{
                  color: theme.colors.primaryTextColor[6],
                  "&:hover": {
                    backgroundColor: theme.colors.primary[4],
                  },
                }}
                loading={createLoading || updateLoading}
              >
                {formData?.id ? "Save" : "Create"}
              </Button>
            </Group>
          </>
        )}
      </FormCard>
    </>
  );
};

export default memo(AccessPeriodDetail);
